import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, Drawer } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  CarOutlined,
  FileTextOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { showAuthAlert, hideAuthAlert } from '../../store/slices/authAlertSlice';
import { AuthAlert } from '../ui/AuthAlert';
import { setAuthErrorCallback } from '../../utils/simpleApiClient';

const { Header, Sider, Content } = Layout;

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { isVisible: isAuthAlertVisible, shouldRetry } = useSelector((state: RootState) => state.authAlert);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/parkings',
      icon: <CarOutlined />,
      label: 'Parkings',
    },
    {
      key: '/tickets',
      icon: <FileTextOutlined />,
      label: 'Tickets',
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: 'Utilisateurs',
    },
    {
      key: '/tracking',
      icon: <BarChartOutlined />,
      label: 'Data Tracking',
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Paramètres',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    if (isMobile) {
      setMobileMenuVisible(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  // Configuration du callback pour les erreurs d'authentification
  useEffect(() => {
    setAuthErrorCallback((errorMessage: string) => {
      dispatch(showAuthAlert({ message: errorMessage }));
    });
  }, [dispatch]);

  const handleDismissAuthAlert = () => {
    dispatch(hideAuthAlert());
  };

  const handleRetryAuth = () => {
    dispatch(hideAuthAlert());
    // Optionnel: essayer de rafraîchir le token ou rediriger vers la page de connexion
    window.location.reload();
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profil',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Déconnexion',
      onClick: handleLogout,
    },
  ];

  const menuContent = (
    <>
      <div className="h-16 flex items-center justify-center border-b border-neutral-200 px-4">
        <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
          <span className="text-white font-bold text-lg">P</span>
        </div>
        {(!collapsed || isMobile) && (
          <span className="ml-2 text-lg font-semibold text-neutral-900">
            Tunisie Parking Admin
          </span>
        )}
      </div>
      
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
        className="border-none"
      />
    </>
  );

  return (
    <Layout className="min-h-screen">
      <AuthAlert
        visible={isAuthAlertVisible}
        onDismiss={handleDismissAuthAlert}
        onRetry={shouldRetry ? handleRetryAuth : undefined}
      />
      
      {/* Sidebar Desktop */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          className="bg-white border-r border-neutral-200"
          width={250}
          collapsedWidth={80}
        >
          {menuContent}
        </Sider>
      )}
      
      {/* Drawer Mobile */}
      <Drawer
        title="Menu"
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        bodyStyle={{ padding: 0 }}
        width={250}
      >
        {menuContent}
      </Drawer>
      
      <Layout>
        <Header className="bg-white border-b border-neutral-200 px-2 sm:px-4 flex items-center justify-between">
          <div className="flex items-center flex-1 min-w-0">
            <Button
              type="text"
              icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
              onClick={() => {
                if (isMobile) {
                  setMobileMenuVisible(true);
                } else {
                  setCollapsed(!collapsed);
                }
              }}
              className="mr-2 sm:mr-4 flex-shrink-0"
            />
            <h1 className="text-base sm:text-xl font-semibold text-neutral-900 m-0 truncate">
              {menuItems.find(item => item.key === location.pathname)?.label || 'Dashboard'}
            </h1>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div className="flex items-center cursor-pointer hover:bg-neutral-50 px-2 sm:px-3 py-2 rounded-lg">
                <Avatar size="small" className="bg-primary-500 flex-shrink-0">
                  {user?.phone?.slice(-2) || 'A'}
                </Avatar>
                <span className="ml-2 text-neutral-700 hidden sm:inline">
                  {user?.phone || 'Admin'}
                </span>
              </div>
            </Dropdown>
          </div>
        </Header>
        
        <Content className="bg-neutral-50 p-4 sm:p-6 lg:p-8">
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}
